import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaComments,
  FaMapMarkerAlt,
  FaBullseye,
  FaStore,
  FaStopwatch,
  FaTrophy,
  FaUsers,
} from 'react-icons/fa';
import Seo from '../components/Seo';
import AppStoreButtons from '../components/release/AppStoreButtons';
import { PLATFORM } from '../config/platform';
import practiceRoomIcon from '../assets/android/practice-room.svg';
import quickRaceIcon from '../assets/android/quick-race.svg';
import practiceRoomUi from '../assets/android/practice-room-ui.png';
import './Home.css';

const features = [
  {
    icon: FaBullseye,
    art: practiceRoomIcon,
    title: 'Train in the P4R Practice Room',
    body: 'Use the Android P4R Engine for 9 Ball, 8 Ball, 3 Cushion, Brazilian Sinuca and more, with live physics, shot prediction, Auto Aim, drills, cue/spin control, discipline-specific tables and match-tuned audio.',
    to: '/pool-practice-room',
  },
  {
    icon: FaUsers,
    title: 'Meet pool players nearby',
    body: 'Discover local billiards players, view player profiles, start a conversation, and organize an in-person match at a real table.',
    to: '/find-pool-players-near-me',
  },
  {
    icon: FaStopwatch,
    art: quickRaceIcon,
    title: 'Count races and track matches',
    body: 'Use Race Room as a live pool scorekeeper for racks, race targets, points, innings, timers, player turns, and saved match history.',
    to: '/pool-scorekeeper',
  },
  {
    icon: FaTrophy,
    title: 'Organize teams and tournaments',
    body: 'Create or join teams, discover real tournaments, manage entries and check-in, follow brackets, and record results.',
    to: '/pool-tournaments-near-me',
  },
  {
    icon: FaMapMarkerAlt,
    title: 'Find pool halls and billiards businesses',
    body: 'Explore venues, events, retailers, instructors, cue services, and other businesses that support the real-world sport.',
    to: '/pool-halls-near-me',
  },
  {
    icon: FaComments,
    title: 'Stay connected to the pool community',
    body: 'Share Pockits, message players, build your billiards profile, and keep your local and competitive pool life connected.',
    to: '/billiards-community',
  },
  {
    icon: FaStore,
    title: 'Buy and sell pool equipment',
    body: 'Browse or list cues, cases, tables, balls, racks, accessories, and other billiards gear through Shoparound.',
    to: '/buy-sell-pool-equipment',
  },
];

const softwareSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: PLATFORM.name,
  alternateName: 'P4R',
  applicationCategory: 'SocialNetworkingApplication',
  applicationSubCategory: 'Billiards and pool sports community',
  operatingSystem: 'iOS, Android, Web',
  url: PLATFORM.siteUrl,
  downloadUrl: [PLATFORM.ios.url, PLATFORM.android.url],
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  audience: {
    '@type': 'Audience',
    audienceType: 'Pool players, billiards players, tournament organizers, teams, pool halls, and billiards businesses',
  },
  featureList: [
    'Find nearby pool and billiards players',
    'Message players and organize real-world matches',
    'P4R Engine Practice Room with multi-discipline physics and training tools',
    'Pool race counter, advanced Race Room and live match display',
    'Pool teams and tournament management',
    'Pool hall and billiards business discovery',
    'Billiards social feed and player profiles',
    'Marketplace for pool cues and equipment',
  ],
  description: 'Pool 4 Real is a billiards social, competition, and training platform for finding nearby players, practicing with the P4R Engine, organizing matches, scoring advanced Race Room sessions, joining tournaments, discovering pool halls, and connecting the pool community.',
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: PLATFORM.name,
  alternateName: 'P4R',
  url: PLATFORM.siteUrl,
  logo: `${PLATFORM.siteUrl}/assets/pool4real-app-icon.png`,
  sameAs: [PLATFORM.ios.url, PLATFORM.android.url],
  description: 'A social networking and sports technology platform built for real-world pool and billiards communities.',
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: PLATFORM.name,
  alternateName: 'Pool 4 Real — P4R',
  url: PLATFORM.siteUrl,
  description: 'Find pool players nearby, organize real matches, score races, join tournaments, discover pool halls, and connect with the billiards community.',
};

export default function Home() {
  return (
    <main className="p4r-home">
      <Seo
        title="Pool 4 Real | Billiards Practice Room, Race Room & Player Community"
        description="Pool 4 Real combines a billiards social network with the Android P4R Engine Practice Room, advanced Race Room scorekeeping, nearby players, teams, tournaments, pool halls, messages and Shoparound."
        schema={[softwareSchema, organizationSchema, websiteSchema]}
      />

      <section className="p4r-home-hero">
        <div className="p4r-home-hero-copy">
          <p className="p4r-home-eyebrow">The social network for real-world pool and billiards</p>
          <h1>Find players. Train smarter. Run the race.</h1>
          <p className="p4r-home-lead">
            Pool 4 Real connects real billiards players and now adds serious play tools on Android: train in the P4R Engine Practice Room, run advanced Race Room sessions, meet nearby players, message, build teams, enter tournaments, discover venues, and stay connected to the sport.
          </p>
          <div className="p4r-home-actions">
            <Link className="p4r-home-primary" to="/find-pool-players-near-me">Find pool players nearby <FaArrowRight /></Link>
            <Link className="p4r-home-secondary" to="/pool-practice-room">Explore Practice Room</Link>
          </div>
          <AppStoreButtons source="home_hero" />
          <p className="p4r-home-availability">Official Pool 4 Real apps are available for iPhone and Android, with the same P4R community account across supported platforms.</p>
        </div>
        <div className="p4r-home-visual" aria-label="Pool 4 Real mobile app for real-world billiards players">
          <div className="p4r-home-orbit orbit-one" />
          <div className="p4r-home-orbit orbit-two" />
          <img src="/assets/pool4real-app-icon.png" alt="Pool 4 Real billiards social networking app icon" />
          <div className="p4r-home-proof proof-a"><strong>Connect</strong><span>Nearby pool players</span></div>
          <div className="p4r-home-proof proof-b"><strong>Organize</strong><span>Matches & tournaments</span></div>
          <div className="p4r-home-proof proof-c"><strong>Count</strong><span>Races & scores</span></div>
        </div>
      </section>

      <section className="p4r-home-positioning">
        <p>Built around real billiards—with serious digital training tools</p>
        <h2>Meet, practice, play, compete, score, and manage billiards from one connected platform.</h2>
        <p>Pool 4 Real combines real-world player/community tools with the Android P4R Engine Practice Room and an advanced Race Room for live matches, scorekeeping, timers, innings, player turns, shared sessions, and full-screen displays.</p>
      </section>

      <section className="p4r-home-features" aria-labelledby="everything-heading">
        <div className="p4r-home-section-head">
          <p className="p4r-home-eyebrow">One platform around the full billiards experience</p>
          <h2 id="everything-heading">From finding an opponent to recording the final rack</h2>
        </div>
        <div className="p4r-home-feature-grid">
          {features.map(({ icon: Icon, art, title, body, to }) => (
            <Link to={to} className="p4r-home-feature" key={title}>
              {art ? <img className="p4r-home-feature-art" src={art} alt="" /> : <Icon />}
              <h3>{title}</h3>
              <p>{body}</p>
              <span>Learn more <FaArrowRight /></span>
            </Link>
          ))}
        </div>
      </section>

      <section className="p4r-android-showcase" aria-labelledby="android-tools-heading">
        <div className="p4r-android-showcase-copy">
          <p className="p4r-home-eyebrow">Advanced Android billiards tools</p>
          <h2 id="android-tools-heading">Practice Room + Race Room</h2>
          <p>The Android app is the reference implementation for P4R's competition and training experience. Practice Room runs the P4R Engine with discipline-specific physics, prediction, drills and gameplay audio; Race Room handles live scoring, timers, innings, shared control and a readable full-screen table display.</p>
          <div className="p4r-home-actions">
            <Link className="p4r-home-primary" to="/pool-practice-room">Practice Room <FaArrowRight /></Link>
            <Link className="p4r-home-secondary" to="/pool-scorekeeper">Race Room</Link>
          </div>
        </div>
        <div className="p4r-android-showcase-visual"><img src={practiceRoomUi} alt="Pool 4 Real Android Practice Room interface" /></div>
      </section>

      <section className="p4r-home-searches">
        <div>
          <p className="p4r-home-eyebrow">Find the billiards tool or community you need</p>
          <h2>What would help you play more pool?</h2>
        </div>
        <nav aria-label="Popular pool and billiards searches">
          <Link to="/find-pool-players-near-me">Find pool players near me</Link>
          <Link to="/organize-pool-matches">Organize a pool match</Link>
          <Link to="/pool-scorekeeper">Pool race counter and scorekeeper</Link>
          <Link to="/pool-match-tracker">Track pool match scores</Link>
          <Link to="/pool-tournaments-near-me">Pool tournaments near me</Link>
          <Link to="/pool-halls-near-me">Pool halls near me</Link>
          <Link to="/pool-teams">Create or join a pool team</Link>
          <Link to="/pool-hall-management">Tools for pool halls and organizers</Link>
          <Link to="/buy-sell-pool-equipment">Buy and sell pool equipment</Link>
          <Link to="/billiards-community">Billiards social networking app</Link>
        </nav>
      </section>

      <section className="p4r-home-final">
        <img src="/assets/pool4real-logo.png" alt="Pool 4 Real logo" />
        <div>
          <p className="p4r-home-eyebrow">Real players. Real tables. Real matches.</p>
          <h2>Bring your billiards community and match tools together.</h2>
          <p>Use Pool 4 Real on the web and carry your player network, conversations, competitions, and Race Room tools with you on iPhone and Android.</p>
          <AppStoreButtons source="home_footer_cta" />
        </div>
      </section>
    </main>
  );
}
